/* eslint-disable react/prop-types */
import React, {
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  MaterialReactTable,
  MRT_Cell as MRTCell,
  MRT_Column as MRTColumn,
  type MRT_ColumnDef as MRTColumnDef,
  MRT_ColumnOrderState as MRTColumnOrderState,
  MRT_DensityState as MRTDensityState,
  MRT_GroupingState as MRTGroupingState,
  MRT_Icons as MRTIcons,
  MRT_Row as MRTRow,
  MRT_RowData as MRTRowData,
  MRT_ShowHideColumnsButtonProps as MRTShowHideColumnsButtonProps,
  MRT_TableInstance as MRTTableInstance,
  MRT_TableOptions as MRTTableOptions,
  MRT_ToggleDensePaddingButton as MRTToggleDensePaddingButton,
  MRT_ToggleFiltersButton as MRTToggleFiltersButton,
  MRT_ToggleGlobalFilterButton as MRTToggleGlobalFilterButton,
  MRT_VisibilityState as MRTVisibilityState,
  isCellEditable,
  useMaterialReactTable,
} from "material-react-table";
import { TestID, TestIDWrapper } from "../../shared/testids/testids";
import { CsvNumberKind } from "../../shared/unitFormatting";
import { CORECSVButton } from "../Action/CORECSVButton";
import { COREIcon } from "./COREIcon";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { COREPortal } from "../Layout/COREPortal";
import "./CORETable.less";
import "./MaterialTableCustomStyle/MRTCheckbox.less";
import "./MaterialTableCustomStyle/MRTMenu.less";
import "./MaterialTableCustomStyle/MRTTooltip.less";
import { Col, Row, Space } from "antd";
import { CORETag } from "./CORETag";
import { COREBody, COREBodyTruncateWithTooltip } from "../Typography/COREBody";
import { grey100, grey120, white } from "./COREColour";
import { COREPagination } from "../Navigation/COREPagination";
import { COREDivider } from "../Layout/COREDivider";
import classNames from "classnames";
import { CORETooltip } from "../Overlay/CORETooltip";
import { COREButton } from "../Action/COREButton";
import { COREDropdownMenu, DropdownMenuItem } from "../Form/COREDropdownMenu";
import { COREMenuItem } from "../Navigation/COREMenuItem";
import { CORESwitch } from "../Form/CORESwitch";
import { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { FullScreenModeContext } from "../../shared/state/fullScreenModeContext";
import DensityLargeIcon from "@mui/icons-material/DensityLarge";
import DensityMediumIcon from "@mui/icons-material/DensityMedium";
import DensitySmallIcon from "@mui/icons-material/DensitySmall";
import { CORELoading } from "../Feedback/CORELoading";
import { Link } from "react-router-dom";
import {
  LocalStorageUntilLogoutKey,
  useLocalStorageUntilLogoutPerKey,
} from "../../shared/customHoooks/useLocalStorageUntilLogout";
import { useDeepCompareEffect, useLocalStorage } from "react-use";
import { useLocalStorageWithUpdater } from "../../shared/customHoooks/useLocalStorageWithUpdater";
import { URLSyncedTableStateSync } from "../../shared/customHoooks/useURLSyncedTableState";
import {
  ColumnOrderState,
  GroupingState,
  OnChangeFn,
  PaginationState,
  SortingState,
  Updater,
  VisibilityState,
} from "@tanstack/table-core";
import { COREModal, COREModalProps } from "../Overlay/COREModal";
import { CORESelect } from "../Form/CORESelect";
import { spacing, useOpenClose } from "../../shared/global";
import {
  UserPreference,
  useUserConfigs,
} from "../../shared/customHoooks/useUserConfig";
import {
  buildCustomStripedRowsById,
  getIsStripedRow,
  mergeTableRowProps,
} from "./CORETableRowStyling";
import type {
  CORETableRowStripingStrategy,
  CORETableRowStyleResolverResult,
} from "./CORETableRowStyling";

export type CellProps<TData extends MRTRowData> = {
  cell: MRTCell<TData, unknown>;
  column: MRTColumn<TData, unknown>;
  renderedCellValue: ReactNode;
  row: MRTRow<TData>;
  staticColumnIndex?: number;
  staticRowIndex?: number;
  rowRef?: RefObject<HTMLTableRowElement>;
  table: MRTTableInstance<TData>;
};

export type CORETableColumn<T extends Record<string, unknown>> =
  MRTColumnDef<T> & {
    // Optional label shown for this column in the show/hide columns dropdown
    // when it should differ from the column header (the menu is also sorted by
    // this label). Falls back to `header` when not provided.
    showHideMenuLabel?: string;
    // Raw numeric value used for CSV export only. `accessorFn` returns the
    // formatted display string; CSV must export the underlying numeric value.
    // Must return null for premium-locked cells so raw values never leak.
    csvRawAccessorFn?: (row: T) => number | string | null;
    // How the raw CSV value is serialized (decimal places / percent scaling).
    // Paired with `csvRawAccessorFn` on numeric columns; defaults to "number".
    csvNumberKind?: CsvNumberKind;
    // Unit suffix appended to this column's CSV header (never to the on-screen
    // header), e.g. "MW" renders the CSV header as "Max capacity (MW)". Omit
    // for dimensionless columns (rankings, counts).
    csvUnit?: string;
    // Render the cell without the `rowLink` wrapper, for controls that link
    // somewhere other than the row's destination.
    disableRowLink?: boolean;
  };
export type {
  CORETableRowStripingStrategy,
  CORETableRowStripingContext,
  CORETableRowStyleResolverResult,
  CORETableRowStyleVariant,
} from "./CORETableRowStyling";

export type CORETableProps<RecordType extends Record<string, unknown>> =
  MRTTableOptions<RecordType> & {
    testID: TestID;
    theme?: "default" | "blue";
    enableRowStriping?: boolean;
    enableDownloadCSV?: boolean;
    csvFileName?: string;
    onRow?: (values: RecordType) => void;
    rowLink?: (values: RecordType) => string;
    customCSVData?: {
      headers:
        | Partial<Record<keyof RecordType, string>>
        | ((
            table: MRTTableInstance<RecordType>
          ) => Partial<Record<keyof RecordType, string>>);
      transform: (
        csvData: RecordType[],
        table?: MRTTableInstance<RecordType>
      ) => Record<string, unknown>[];
    };
    localStorageKey: LocalStorageUntilLogoutKey;
    /**
     * When provided, sorting, pagination and column visibility are also mirrored
     * to this URL search param so the current view can be shared or bookmarked.
     * An existing param wins over the localStorage preference, and opening a
     * shared link therefore overwrites the recipient's own saved sorting, column
     * visibility and page size — by design, matching useURLSyncedStateBase, but
     * worth knowing before opting another table in.
     *
     * Deliberately not called `urlKey`: `CORETableHook` already takes a `urlKey`
     * for its own, pagination-only `TableOptions` param, and one name meaning
     * two different payloads let a caller pass it to the hook and silently get
     * the other behaviour.
     *
     * The pagination half only covers the client-side pagination this component
     * owns. A table driven by `CORETableHook` turns that off (`enablePagination:
     * false` plus a `customPagination`), so there it mirrors sorting and column
     * visibility only and the page has to come from the hook's own `urlKey` — a
     * pagination slice in this param would be hydrated into localStorage that
     * nothing reads, with no reset or clamping behind it.
     */
    viewStateURLKey?: string;
    enableRememberPaginate?: boolean;
    enableRememberPageSizeEvenLogginOut?: boolean;
    enableRememberColumnOrder?: boolean;
    enableRememberColumnVisibility?: boolean;
    enableRememberDensity?: boolean;
    enableRememberSorting?: boolean;
    enableRememberGrouping?: boolean;
    isLoading?: boolean;
    customPagination?: ReactNode;
    bulkActions?: BulkActionsType<RecordType>[];
    defaultPaginate?: TableSettingUntilLogout["pagination"];
    customDownloadCSVButton?: ReactNode;
    editDisplayMode?: MRTTableOptions<RecordType>["editDisplayMode"];
    tableRef?: React.MutableRefObject<HTMLDivElement | null>;
    clickToEditMode?: "single" | "double";
    setIsEditingForDatasheet?: React.Dispatch<React.SetStateAction<boolean>>;
    externalToolbarContainerId?: string;
    externalToolbarWrapper?: (children: ReactNode) => ReactNode;
    rowStyleResolver?: (params: {
      row: MRTRow<RecordType>;
      rowIndex: number;
      isStripedRow: boolean;
    }) => CORETableRowStyleResolverResult;
    rowStripingStrategy?: CORETableRowStripingStrategy<RecordType>;
  };

type GroupedCellProps<RecordType extends Record<string, unknown>> = {
  cell: MRTCell<RecordType, unknown>;
  column: MRTColumn<RecordType, unknown>;
  row: MRTRow<RecordType>;
  table: MRTTableInstance<RecordType>;
};

type TableSettingUntilLogout = {
  pagination?: {
    pageSize: number;
    pageIndex: number;
  };
};

type TableSetting = {
  grouping?: MRTGroupingState;
  columnOrder?: MRTColumnOrderState;
  columnVisibility?: MRTVisibilityState;
  density?: MRTDensityState;
  sorting?: SortingState;
};

export type CustomToolbarMenuProps<T extends Record<string, unknown>> = {
  table: MRTTableInstance<T>;
  isPortalUsed?: boolean;
} & Pick<
  CORETableProps<T>,
  | "testID"
  | "data"
  | "enableDownloadCSV"
  | "csvFileName"
  | "customCSVData"
  | "customDownloadCSVButton"
  | "externalToolbarWrapper"
>;

export const MRT_DENSITY_STATES: MRTDensityState[] = [
  "compact",
  "comfortable",
  "spacious",
] as const;

export const alignColumn = <T extends Record<string, unknown>>(
  align: "center" | "left" | "right"
): Pick<
  CORETableColumn<T>,
  "muiTableBodyCellProps" | "muiTableHeadCellProps"
> => ({
  muiTableBodyCellProps: {
    align: align,
  },
  muiTableHeadCellProps: {
    align: align,
  },
});

export const mapDataToVisibleColumn = <T extends Record<string, unknown>>(
  data: T[],
  keys: string[]
) => {
  return data.map((item) =>
    keys.map(
      (key) =>
        key.split(".").reduce<unknown>((acc, k) => {
          if (acc && typeof acc === "object" && k in acc) {
            return (acc as Record<string, unknown>)[k] ?? "";
          }
          return "";
        }, item) as string | number
    )
  );
};

export const gettingVisibleColumnKeys = <
  T extends Record<string, unknown> = {}
>(
  columns: ReturnType<
    CustomToolbarMenuProps<T>["table"]["getVisibleFlatColumns"]
  >
) => {
  return columns
    .filter((t) => t && t.id && !t.id.startsWith("mrt-row"))
    .map((t) => t.id);
};

export const gettingVisibleColumnHeaders = <
  T extends Record<string, unknown> = {}
>(
  columns: ReturnType<
    CustomToolbarMenuProps<T>["table"]["getVisibleFlatColumns"]
  >
) => {
  return columns
    .filter((t) => t && t.id && !t.id.startsWith("mrt-row"))
    .map((t) => t.columnDef.header as string);
};

type FullscreenButtonProps<T extends Record<string, unknown>> = {
  table: MRTTableInstance<T>;
  testID: TestID;
};

const FullscreenButton = <T extends Record<string, unknown>>({
  table,
  testID,
}: FullscreenButtonProps<T>) => {
  const { isFullScreenMode = false, setIsFullScreenMode } = useContext(
    FullScreenModeContext
  );

  const {
    options: {
      icons: { FullscreenExitIcon, FullscreenIcon },
    },
  } = table;

  return (
    <CORETooltip
      position="bottom"
      width="auto"
      title=""
      message="Toggle full screen"
      testID={`${testID}-full-screen-tooltip`}
    >
      <COREButton
        type="action"
        size="lg"
        icon={isFullScreenMode ? <FullscreenExitIcon /> : <FullscreenIcon />}
        onClick={() => {
          setIsFullScreenMode && setIsFullScreenMode(!isFullScreenMode);
          table.setIsFullScreen(!isFullScreenMode);
        }}
        testID={`${testID}-full-screen-button`}
      />
    </CORETooltip>
  );
};

const getCustomCSVData = <T extends Record<string, unknown>>(
  customCSVData: CORETableProps<T>["customCSVData"],
  table: MRTTableInstance<T>
): {
  keys: string[];
  headers: string[];
  transform: (d: T[], table?: MRTTableInstance<T>) => Record<string, unknown>[];
} => {
  const { headers: headersConfig, transform } = customCSVData ?? {};

  const headers =
    typeof headersConfig === "function"
      ? headersConfig(table)
      : headersConfig ?? {};

  return {
    keys: Object.keys(headers),
    headers: Object.values(headers) as string[],
    transform: transform ?? ((d: T[]) => d),
  };
};

const DownloadCSVButton = <T extends Record<string, unknown> = {}>({
  table,
  data,
  testID,
  customCSVData,
  csvFileName = "CSV Data",
}: CustomToolbarMenuProps<T>) => {
  const getData = useCallback(() => {
    const columns = table.getVisibleFlatColumns();
    const { keys, headers, transform } = getCustomCSVData(customCSVData, table);
    const dataResult = transform ? transform(data, table) : data;

    const visibleColumnKeys = keys.length
      ? keys
      : gettingVisibleColumnKeys(columns);
    const visibleColumnHeaders = headers.length
      ? headers
      : gettingVisibleColumnHeaders(columns);
    const dataSource = mapDataToVisibleColumn(dataResult, visibleColumnKeys);

    return {
      headers: visibleColumnHeaders,
      data: dataSource,
    };
  }, [table, data, customCSVData]);

  return (
    <CORECSVButton
      testID={`${testID}-csv-download`}
      filename={csvFileName}
      getData={getData}
      icon={<COREIcon icon={icon({ name: "file-csv", style: "regular" })} />}
      key={`${testID}-csv-download`}
      buttonType="action"
      buttonSize="lg"
    />
  );
};

const CustomToolbarMenu = <T extends Record<string, unknown>>({
  table,
  data,
  testID,
  enableDownloadCSV,
  csvFileName,
  customCSVData,
  customDownloadCSVButton,
  isPortalUsed,
  externalToolbarWrapper,
}: CustomToolbarMenuProps<T>) => {
  const {
    options: {
      columnFilterDisplayMode,
      enableColumnFilters,
      enableDensityToggle,
      enableFilters,
      enableFullScreenToggle,
      enableGlobalFilter,
      enableHiding,
      initialState,
    },
  } = table;

  const defaultSpaceWrapper = (children: ReactNode) => (
    <Space size={0}>{children}</Space>
  );

  const shouldWrap = isPortalUsed || externalToolbarWrapper;
  const wrapper = externalToolbarWrapper || defaultSpaceWrapper;
  const toolbarContent = (
    <>
      {enableFilters &&
        enableGlobalFilter &&
        !initialState?.showGlobalFilter && (
          <MRTToggleGlobalFilterButton table={table} />
        )}
      {enableFilters &&
        enableColumnFilters &&
        columnFilterDisplayMode !== "popover" && (
          <MRTToggleFiltersButton table={table} />
        )}
      {enableHiding && (
        <CustomShowHideColumnsButton table={table} testID={testID} />
      )}
      {enableDensityToggle && <MRTToggleDensePaddingButton table={table} />}
      {enableFullScreenToggle && (
        <FullscreenButton table={table} testID={testID} />
      )}
      {enableDownloadCSV && (
        <CORETooltip
          position="bottom"
          width="auto"
          title=""
          message="Download CSV"
          testID={`${testID}-csv-tooltip`}
        >
          {customDownloadCSVButton ? (
            customDownloadCSVButton
          ) : (
            <DownloadCSVButton
              table={table}
              data={data}
              testID={testID}
              csvFileName={csvFileName}
              customCSVData={customCSVData}
            />
          )}
        </CORETooltip>
      )}
    </>
  );
  return shouldWrap ? wrapper(toolbarContent) : toolbarContent;
};

type CustomShowHideColumnsButtonProps<TData extends MRTRowData> = {
  testID: TestID;
  table: MRTShowHideColumnsButtonProps<TData>["table"];
};

const getShowHideMenuLabel = (columnDef: {
  header?: unknown;
  showHideMenuLabel?: string;
}): string => {
  if (typeof columnDef.showHideMenuLabel === "string") {
    return columnDef.showHideMenuLabel;
  }
  return typeof columnDef.header === "string" ? columnDef.header : "";
};

const CustomShowHideColumnsButton = <TData extends MRTRowData>({
  table,
  testID,
}: CustomShowHideColumnsButtonProps<TData>) => {
  const {
    options: {
      icons: { ViewColumnIcon },
    },
  } = table;
  // The popup is portalled to document.body, so it escapes the full screen paper's
  // stacking context and needs its own z-index to sit above it.
  const { isFullScreen } = table.getState();
  const columns = table
    .getAllColumns()
    .filter((c) => {
      const header = c.columnDef.header;
      const isHeaderEmpty =
        typeof header === "string" ? !header.trim() : !header;
      return (
        c.columnDef.columnDefType !== "display" &&
        c.columnDef.visibleInShowHideMenu !== false &&
        !isHeaderEmpty
      );
    })
    .sort((a, b) => {
      return getShowHideMenuLabel(a.columnDef).localeCompare(
        getShowHideMenuLabel(b.columnDef)
      );
    });
  const currentColumnVisibility = columns.reduce(
    (acc: Record<string, boolean>, c) => {
      const id = c.columnDef.id ?? c.columnDef.accessorKey;
      acc[id as string] = c.getIsVisible();
      return acc;
    },
    { "mrt-row-expand": false }
  );

  const menus: DropdownMenuItem[] = columns.map((c) => {
    const id = c.columnDef.id ?? c.columnDef.accessorKey;
    return {
      closeWhenClick: false,
      component: (
        <COREMenuItem testID={`${testID}-menu-item`} key={id}>
          <Space size={8} direction="horizontal">
            <CORESwitch
              size="sm"
              checked={c.getIsVisible()}
              onChange={(value) => {
                if (id) {
                  table.setColumnVisibility({
                    ...currentColumnVisibility,
                    [id]: value,
                  });
                }
              }}
              testID={`${testID}-menu-item-switch-${id}`}
              disabled={!c.getCanHide()}
            />
            <span
              className="show-hide-column-label"
              title={getShowHideMenuLabel(c.columnDef)}
            >
              <COREBody marginBottom={false} type="p3">
                {getShowHideMenuLabel(c.columnDef)}
              </COREBody>
            </span>
          </Space>
        </COREMenuItem>
      ),
    };
  });

  return (
    <>
      <COREDropdownMenu
        interactionsMenu={menus}
        testID={`${testID}-show-hide-column-dropdown`}
        isDisabled={false}
        // The button sits at the right end of the toolbar, so align the menu's right
        // edge to it and let it open leftwards. bottomLeft anchored the menu's left
        // edge and relied on antd shifting it back to fit, which is what fails in
        // full screen (#2331).
        placement="bottomRight"
        menuMaxHeight={400}
        className={classNames("core-table-show-hide-columns-menu", {
          "core-table-show-hide-columns-full-screen": isFullScreen,
        })}
      >
        <COREButton
          displayTooltip
          tooltipTitle="Show/Hide columns"
          tooltipPosition="bottom"
          tooltipWidth="auto"
          type="action"
          size="lg"
          icon={<ViewColumnIcon />}
          testID={`${testID}-show-hide-button`}
        />
      </COREDropdownMenu>
    </>
  );
};

type CustomColumnGroupingToolbarProps<T extends Record<string, unknown>> = {
  table: MRTTableInstance<T>;
  columns: CORETableProps<T>["columns"];
  testID: TestID;
};

const CustomColumnGroupingToolbar = <T extends Record<string, unknown>>({
  table,
  columns,
  testID,
}: CustomColumnGroupingToolbarProps<T>) => {
  const { grouping } = table.getState();
  const { setGrouping } = table;
  const groupingColumns = grouping.map((group) =>
    columns.find((column) => column.accessorKey === group)
  );
  const handleCloseTag = (accessorKey: string) => {
    setGrouping(grouping.filter((item) => item !== accessorKey));
  };
  return (
    <Space>
      <COREBody
        testID={`${testID}-grouped-by-title`}
        type="p3"
        strong
        marginBottom={false}
      >
        Grouped by
      </COREBody>
      {groupingColumns.map((groupingColumn) => (
        <CORETag
          testID={`${testID}-group-tag-${groupingColumn?.accessorKey}`}
          key={groupingColumn?.header}
          type="basic"
          label={groupingColumn?.header}
          closable
          onClose={() => {
            groupingColumn?.accessorKey &&
              handleCloseTag(groupingColumn?.accessorKey);
          }}
        />
      ))}
    </Space>
  );
};

type IconProps = JSX.IntrinsicAttributes &
  Omit<FontAwesomeIconProps, "size"> & {
    size?: "lg" | "md" | "sm" | "xxs" | "xs" | "xl" | "xxl";
    animate?:
      | "none"
      | "spin"
      | "spinPulse"
      | "spinReverse"
      | "pulse"
      | "beat"
      | "fade"
      | "beatFade"
      | "bounce"
      | "shake";
  } & {
    children?: React.ReactNode | undefined;
  };

const customizeIcon: Partial<MRTIcons> = {
  ArrowDownwardIcon: (props: IconProps) => {
    return (
      <COREIcon
        {...props}
        className={classNames(
          `core-table-column-header-icon`,
          props?.className
        )}
        icon={icon({ name: "arrow-down", style: "regular" })}
      />
    );
  },
  // ArrowRightIcon: ,
  // CancelIcon: ,
  // ChevronLeftIcon: ,
  // ChevronRightIcon: ,
  ClearAllIcon: (props: IconProps) => (
    <COREIcon
      color={grey120}
      {...props}
      icon={icon({ name: "bars-staggered", style: "regular" })}
    />
  ),
  // CloseIcon: ,
  // ContentCopy: ,
  DensityLargeIcon: (props: IconProps) => (
    <DensityLargeIcon className="core-table-density-icon-color" />
  ),
  DensityMediumIcon: (props: IconProps) => (
    <DensityMediumIcon className="core-table-density-icon-color" />
  ),
  DensitySmallIcon: (props: IconProps) => (
    <DensitySmallIcon className="core-table-density-icon-color" />
  ),
  DragHandleIcon: (props: IconProps) => (
    <COREIcon
      {...props}
      className={classNames(`core-table-column-header-icon`, props?.className)}
      icon={icon({ name: "grip-lines", style: "regular" })}
    />
  ),
  DynamicFeedIcon: (props: IconProps) => (
    <COREIcon
      color={grey120}
      {...props}
      icon={icon({ name: "window-restore", style: "solid" })}
    />
  ),
  // EditIcon: ,
  ExpandMoreIcon: (props: IconProps) => (
    <COREIcon
      color={grey100}
      {...props}
      icon={icon({ name: "caret-up", style: "solid" })}
    />
  ),
  // FilterAltIcon: ,
  // FilterListIcon: ,
  // FilterListOffIcon: ,
  // FirstPageIcon: ,
  FullscreenExitIcon: (props: IconProps) => (
    <COREIcon
      color={grey120}
      {...props}
      icon={icon({ name: "compress", style: "regular" })}
    />
  ),
  FullscreenIcon: (props: IconProps) => (
    <COREIcon
      color={grey120}
      {...props}
      icon={icon({ name: "expand", style: "regular" })}
    />
  ),
  // KeyboardDoubleArrowDownIcon: ,
  // LastPageIcon: ,
  // MoreHorizIcon: ,
  MoreVertIcon: (props: IconProps) => (
    <COREIcon
      {...props}
      className={classNames(`core-table-column-header-icon`, props?.className)}
      icon={icon({ name: "ellipsis-vertical", style: "regular" })}
    />
  ),
  // PushPinIcon: ,
  RestartAltIcon: (props: IconProps) => (
    <COREIcon
      color={grey120}
      {...props}
      icon={icon({ name: "arrow-rotate-left", style: "regular" })}
    />
  ),
  // SaveIcon: ,
  // SearchIcon: ,
  // SearchOffIcon: ,
  SortIcon: (props: IconProps) => (
    <COREIcon
      color={grey120}
      {...props}
      icon={icon({ name: "bars-sort", style: "regular" })}
    />
  ),
  SyncAltIcon: (props: IconProps) => (
    <COREIcon
      {...props}
      style={{
        ...props.style,
        transform: `rotate(0deg) scaleX(0.9) translateX(-1px)`,
      }}
      className={classNames(`core-table-column-header-icon`, props?.className)}
      icon={icon({ name: "arrow-up-arrow-down", style: "regular" })}
    />
  ),
  ViewColumnIcon: (props: IconProps) => (
    <COREIcon
      color={grey120}
      {...props}
      icon={icon({ name: "columns-3", style: "regular" })}
    />
  ),
  VisibilityOffIcon: (props: IconProps) => (
    <COREIcon
      color={grey120}
      {...props}
      icon={icon({ name: "eye-slash", style: "regular" })}
    />
  ),
};

type TablePaginationProps<T extends Record<string, unknown>> = {
  table: MRTTableInstance<T>;
  testID: TestID;
  setTableSettingUntilLogout?: (values: TableSettingUntilLogout) => void;
  setPageSizePermanent?: (value: number) => void;
  enableRememberPageSizeEvenLogginOut?: boolean;
};

const TablePagination = <T extends Record<string, unknown>>({
  table,
  testID,
  setTableSettingUntilLogout,
  setPageSizePermanent,
  enableRememberPageSizeEvenLogginOut,
}: TablePaginationProps<T>) => {
  const {
    pagination: { pageIndex, pageSize },
  } = table.getState();
  const total = table.getRowCount();
  return (
    <COREPagination
      testID={`${testID}-pagination`}
      total={total}
      current={pageIndex + 1}
      showSizeChanger
      pageSize={pageSize}
      onChange={(newPage, newPageSize) => {
        const pageIndex = newPage - 1;
        table.setPageIndex(pageIndex);
        table.setPageSize(newPageSize);
        setTableSettingUntilLogout &&
          setTableSettingUntilLogout({
            pagination: {
              pageIndex: pageIndex,
              pageSize: newPageSize,
            },
          });
        if (enableRememberPageSizeEvenLogginOut && setPageSizePermanent) {
          setPageSizePermanent(newPageSize);
        }
      }}
    />
  );
};

export const getHeightByDensity = (density: MRTDensityState) => {
  switch (density) {
    case "compact":
      return "30px";
    case "comfortable":
      return "38px";
    case "spacious":
      return "46px";
    default:
      return "38px";
  }
};

export type BulkActionsType<RecordType> = {
  label: React.ReactNode;
  key: string;
  testID: TestID;
  onUpdate: (records: RecordType[]) => Promise<void>;
  confirmModal: Pick<COREModalProps, "title" | "status"> & {
    content: React.ReactNode;
  };
};

const BulkActionControl = <RecordType extends Record<string, unknown>>({
  bulkActions,
  table,
  selectedBulkAction,
  setSelectedBulkAction,
  resetBulkAction,
  testID,
}: {
  bulkActions: CORETableProps<RecordType>["bulkActions"];
  table: MRTTableInstance<RecordType>;
  selectedBulkAction: BulkActionsType<RecordType> | null;
  setSelectedBulkAction: React.Dispatch<
    React.SetStateAction<BulkActionsType<RecordType> | null>
  >;
  resetBulkAction: () => void;
  testID: CORETableProps<RecordType>["testID"];
}) => {
  const { opener, isOpen, closer } = useOpenClose(false);
  const selectedRows = table.getSelectedRowModel().rows.map((r) => r.original);
  const options = bulkActions?.map((b) => {
    return {
      label: b.label,
      value: b.key,
    };
  });

  const onConfirm = async () => {
    if (selectedBulkAction) {
      await selectedBulkAction.onUpdate(selectedRows);
    }
    closer();
    resetBulkAction();
  };

  return (
    <>
      <Space size={8}>
        <CORESelect
          testID={`${testID}-bulk-selector`}
          placeholder={"Bulk actions"}
          onChange={(value: BulkActionsType<RecordType>["key"]) => {
            const selectedAction = bulkActions?.find((b) => b.key === value);
            setSelectedBulkAction(selectedAction ?? null);
          }}
          options={options}
          value={selectedBulkAction}
        />
        {selectedBulkAction && (
          <>
            <COREButton
              type={"primary"}
              size={"md"}
              icon={
                <COREIcon
                  icon={icon({
                    name: "circle-arrow-right",
                    style: "regular",
                  })}
                />
              }
              danger={true}
              onClick={opener}
              disabled={selectedRows.length === 0}
            >
              Continue
            </COREButton>

            <COREButton
              testID={`${testID}-bulk-cancel`}
              onClick={resetBulkAction}
            >
              Cancel
            </COREButton>
          </>
        )}
      </Space>
      <COREModal
        open={isOpen}
        status={selectedBulkAction?.confirmModal.status ?? "info"}
        testID={`${testID}-bulk-confirm-modal`}
        type={"basic"}
        title={selectedBulkAction?.confirmModal.title ?? "Confirm bulk action"}
        onCancel={closer}
        footer={[
          <COREButton
            type={"primary"}
            size={"lg"}
            icon={
              <COREIcon
                icon={icon({
                  name: "circle-check",
                  style: "regular",
                })}
              />
            }
            danger={true}
            onClick={() => {
              onConfirm();
            }}
            key={"confirm-bulk-action-button"}
          >
            Confirm
          </COREButton>,
          <COREButton size={"lg"} key={"back"} onClick={closer}>
            Cancel
          </COREButton>,
        ]}
      >
        {selectedBulkAction?.confirmModal.content}
      </COREModal>
    </>
  );
};

const getVisibilityColumnSetting = (
  enableRememberColumnVisibility: boolean,
  localVisibilitySetting?: VisibilityState,
  initialVisibilitySetting?: VisibilityState,
  stateVisibilitySetting?: VisibilityState
) => {
  return enableRememberColumnVisibility
    ? { ...initialVisibilitySetting, ...localVisibilitySetting }
    : stateVisibilitySetting
    ? { "mrt-row-expand": false, ...stateVisibilitySetting }
    : undefined;
};

const getColumnOrderSetting = (
  enableRememberColumnOrder: boolean,
  columnKeys: string[],
  nonOrderableColumnKeys: string[],
  localColumnOrderSetting?: ColumnOrderState,
  stateColumnOrderSetting?: ColumnOrderState
) => {
  const savedOrder = (
    enableRememberColumnOrder
      ? localColumnOrderSetting ?? []
      : stateColumnOrderSetting ?? []
  ).filter(
    (key) => columnKeys.includes(key) && !nonOrderableColumnKeys.includes(key)
  );
  const orderableColumnKeys = columnKeys.filter(
    (key) => !nonOrderableColumnKeys.includes(key)
  );
  const newOrderableKeys = orderableColumnKeys.filter(
    (key) => !savedOrder.includes(key)
  );
  const orderableQueue = [...savedOrder, ...newOrderableKeys];

  // Columns like a leading name column or trailing actions column are
  // excluded from reordering (enableColumnOrdering: false), so they must
  // keep their natural position instead of being pushed around by columns
  // that were added to `columnKeys` after the order was last saved.
  let nextOrderableIndex = 0;
  return columnKeys.map((key) =>
    nonOrderableColumnKeys.includes(key)
      ? key
      : orderableQueue[nextOrderableIndex++]
  );
};

/** Index of the last page that exists for `rowCount` rows, never below zero. */
const lastPageIndexOf = (rowCount: number, pageSize: number): number =>
  Math.max(0, Math.ceil(rowCount / pageSize) - 1);

export const CORETable = <RecordType extends Record<string, unknown> = {}>({
  testID,
  theme = "default",
  enableDownloadCSV,
  csvFileName,
  columns,
  data,
  state,
  initialState,
  enableTableHead,
  enableGrouping,
  enablePagination = true,
  enableEditing,
  editDisplayMode,
  onRow,
  rowLink,
  customCSVData,
  localStorageKey,
  viewStateURLKey,
  isLoading,
  enableRememberPaginate = false,
  enableRememberPageSizeEvenLogginOut = false,
  enableRememberColumnOrder = false,
  enableRememberColumnVisibility = false,
  enableRememberDensity = false,
  enableRememberSorting = false,
  enableRememberGrouping = false,
  enableKeyboardShortcuts = false,
  defaultPaginate = {
    pageSize: 20,
    pageIndex: 0,
  },
  customPagination,
  customDownloadCSVButton,
  enableRowSelection,
  bulkActions,
  onRowSelectionChange,
  tableRef,
  enableColumnOrdering,
  enableRowStriping = true,
  clickToEditMode = "single",
  setIsEditingForDatasheet,
  externalToolbarContainerId,
  externalToolbarWrapper,
  rowStyleResolver,
  rowStripingStrategy,
  muiTableBodyRowProps: externalMuiTableBodyRowProps,
  ...props
}: CORETableProps<RecordType>) => {
  const { isFullScreenMode = false } = useContext(FullScreenModeContext);

  const [selectedBulkAction, setSelectedBulkAction] =
    useState<BulkActionsType<RecordType> | null>(null);
  const { add, configs, loading, sync } =
    useUserConfigs<UserPreference>("preference");
  const preferenceConfig = configs[0];
  const addOrUpdateTableConfig = preferenceConfig
    ? preferenceConfig.updateConfig
    : add;
  const defaultConfig = preferenceConfig?.data?.config;
  // prevent if default value from preference api is not correct(old setting is tight)
  const densityPreferenceConfig =
    defaultConfig?.tableFit &&
    MRT_DENSITY_STATES.includes(defaultConfig.tableFit)
      ? defaultConfig.tableFit
      : "compact";
  const isTableBandedRows = defaultConfig
    ? defaultConfig?.bandedTableRows
    : enableRowStriping;

  const initialTableSetting = {
    grouping: initialState?.grouping ?? [],
    columnOrder: initialState?.columnOrder ?? [],
    columnVisibility: initialState
      ? { ...initialState.columnVisibility, "mrt-row-expand": false }
      : {},
    sorting: initialState?.sorting ?? [],
  };
  const columnKeys = useMemo(
    () => columns.map((c) => c.id ?? c.accessorKey).filter(Boolean) as string[],
    [columns]
  );
  const nonOrderableColumnKeys = useMemo(
    () =>
      columns
        .filter((c) => c.enableColumnOrdering === false)
        .map((c) => c.id ?? c.accessorKey)
        .filter(Boolean) as string[],
    [columns]
  );

  const [tableSettingUntilLogout, setTableSettingUntilLogout] =
    useLocalStorageUntilLogoutPerKey<TableSettingUntilLogout | undefined>(
      "coretable",
      {
        key: localStorageKey,
        initialValue: {
          pagination: defaultPaginate,
        },
      }
    );

  const [tableSetting, setTableSetting] = useLocalStorage<TableSetting>(
    `${localStorageKey}-permanent`,
    initialTableSetting
  );

  const [pageSizeSetting, setPageSizeSetting] =
    useLocalStorageWithUpdater<number>(
      `${localStorageKey}-pageSize-permanent`,
      defaultPaginate.pageSize
    );

  const [visibilitySetting, setVisibilitySetting] =
    useLocalStorageWithUpdater<VisibilityState>(
      `${localStorageKey}-visibility-permanent`,
      initialTableSetting.columnVisibility
    );

  const [columnOrderSetting, setColumnOrderSetting] =
    useLocalStorageWithUpdater<ColumnOrderState>(
      `${localStorageKey}-columnOrder-permanent`,
      initialTableSetting?.columnOrder ?? []
    );

  const [densitySetting, setDensitySetting] = useState<MRTDensityState>(
    densityPreferenceConfig
  );

  useEffect(() => {
    if (!loading && sync) {
      setDensitySetting(densityPreferenceConfig);
    }
  }, [densityPreferenceConfig, loading, sync]);

  const columnWithGroup: CORETableProps<RecordType>["columns"] = useMemo(
    () =>
      columns.map((c) => ({
        Header: ({ column, header, table }) => {
          if (column && column.columnDef && column.columnDef.header) {
            return (
              <COREBodyTruncateWithTooltip
                type="p3"
                color={white}
                strong
                marginBottom={false}
                testID={`${testID}-${column.columnDef.header}-header`}
                truncateAfter={1}
              >
                {column.columnDef.header}
              </COREBodyTruncateWithTooltip>
            );
          }
          return null;
        },
        ...c,
        Cell: ({ cell, column, renderedCellValue, row, table }) => {
          const content: React.ReactNode = c.Cell ? (
            c.Cell({ cell, column, renderedCellValue, row, table })
          ) : (
            <COREBodyTruncateWithTooltip
              type="p2"
              marginBottom={false}
              testID={`${testID}-${renderedCellValue}`}
              truncateAfter={1}
              strong={column.getIndex() === 0}
            >
              {renderedCellValue}
            </COREBodyTruncateWithTooltip>
          );

          if (!rowLink || (c as CORETableColumn<RecordType>).disableRowLink)
            return content;

          const href = rowLink(row.original as RecordType);
          if (!href) return content;

          return (
            <Link
              to={href}
              className="core-table-cell-link"
              tabIndex={-1}
              onClick={(e) => {
                // If the click originated from a React portal (e.g. a modal rendered
                // at document.body), e.target is outside our DOM subtree. Prevent
                // rowLink navigation in that case.
                if (!e.currentTarget.contains(e.target as Node)) {
                  e.preventDefault();
                  return;
                }
                const interactiveEl = (e.target as Element).closest(
                  "button, input, select, textarea, a, [role='button'], [role='switch'], .ant-btn, .ant-checkbox, .ant-switch, .ant-select"
                );
                if (interactiveEl && interactiveEl !== e.currentTarget) {
                  e.preventDefault();
                }
              }}
            >
              {content}
            </Link>
          );
        },
        GroupedCell: ({
          cell,
          column,
          row,
          table,
        }: GroupedCellProps<RecordType>) => {
          const renderCell = c.Cell ? (
            c.Cell({
              cell,
              column,
              renderedCellValue: cell.renderValue() as React.ReactNode,
              row,
              table,
            })
          ) : (
            <COREBodyTruncateWithTooltip
              type="p2"
              marginBottom={false}
              testID={`${testID}-cell-value-text`}
              truncateAfter={1}
            >
              {cell.renderValue() as React.ReactNode}
            </COREBodyTruncateWithTooltip>
          );

          const handleToggleExpanded = () => {
            row.toggleExpanded();
          };
          const {
            options: {
              icons: { ExpandMoreIcon },
            },
          } = table;
          const iconRotate = row.getIsExpanded() ? "-180deg" : "90deg";
          const iconStyle = {
            transform: `rotate(${iconRotate})`,
            transition: "transform 150ms",
          };
          return (
            <Space>
              {row.getCanExpand() && (
                <COREButton
                  type={"action"}
                  size={"lg"}
                  onClick={(e) => {
                    handleToggleExpanded();
                    e.stopPropagation();
                  }}
                  icon={<ExpandMoreIcon style={iconStyle} />}
                  testID={`${testID}-png`}
                />
              )}
              {renderCell}
            </Space>
          );
        },
      })),
    [columns, testID, rowLink]
  );

  const updateTableSetting = <T,>(
    updaterOrValue: Updater<T>,
    key: keyof TableSetting
  ) => {
    const values = tableSetting ?? initialTableSetting;
    setTableSetting(() => {
      const newValue =
        updaterOrValue instanceof Function
          ? updaterOrValue(values[key] as T)
          : updaterOrValue;
      return { ...values, [key]: newValue };
    });
  };

  const getLocalStorageTableSetting = (
    enable: boolean,
    key: keyof TableSetting,
    tableSetting?: TableSetting
  ) => {
    const defaultValue = state ? state[key] : undefined;
    return enable && tableSetting && tableSetting[key]
      ? tableSetting[key]
      : defaultValue;
  };

  const initPagination =
    tableSettingUntilLogout?.pagination ?? state?.pagination ?? defaultPaginate;

  const paginationState: PaginationState | undefined = enableRememberPaginate
    ? {
        pageIndex: initPagination.pageIndex,
        pageSize: enableRememberPageSizeEvenLogginOut
          ? pageSizeSetting ?? defaultPaginate.pageSize
          : initPagination.pageSize,
      }
    : undefined;
  const columnVisibilityState = getVisibilityColumnSetting(
    enableRememberColumnVisibility,
    visibilitySetting,
    initialTableSetting.columnVisibility,
    state?.columnVisibility
  );
  const columnOrderState = getColumnOrderSetting(
    enableRememberColumnOrder,
    columnKeys,
    nonOrderableColumnKeys,
    columnOrderSetting,
    state?.columnOrder
  );
  const groupingState = getLocalStorageTableSetting(
    enableRememberGrouping,
    "grouping",
    tableSetting
  ) as GroupingState | undefined;
  const sortingState = getLocalStorageTableSetting(
    enableRememberSorting,
    "sorting",
    tableSetting
  ) as SortingState | undefined;

  const skipPaginationResetFromURLRef = useRef(false);

  const modifyColumGroup: MRTColumnDef<RecordType, any>[] = useMemo(
    () =>
      columnWithGroup.map((column) => {
        if (
          groupingState &&
          groupingState.includes(column.accessorKey as string)
        ) {
          return {
            ...column,
            muiTableBodyCellProps: {
              ...(column.muiTableBodyCellProps || {}),
              align: "left",
            },
          };
        }
        return column;
      }),
    [columnWithGroup, groupingState]
  );

  const displayColums = useMemo(
    () =>
      groupingState && groupingState.length > 0
        ? modifyColumGroup
        : columnWithGroup,
    [groupingState, modifyColumGroup, columnWithGroup]
  );

  const tableState = {
    isLoading: isLoading || loading,
    density: densitySetting,
    ...(columnVisibilityState && {
      columnVisibility: {
        ...columnVisibilityState,
        "mrt-row-expand": false,
      },
    }),
    ...(paginationState && { pagination: paginationState }),
    ...(columnOrderState && { columnOrder: columnOrderState }),
    ...(groupingState && { grouping: groupingState }),
    ...(sortingState && { sorting: sortingState }),
    ...state,
  };

  const tableInitState = {
    ...initialState,
    density: densitySetting,
    columnVisibility: {
      ...initialState?.columnVisibility,
      "mrt-row-expand": false,
    },
    pagination: initialState?.pagination
      ? initialState?.pagination
      : defaultPaginate,
  };

  const isCustomRowStripingEnabled =
    isTableBandedRows && rowStripingStrategy?.mode === "resetByPredicate";
  const isDefaultRowStripingEnabled =
    isTableBandedRows && !isCustomRowStripingEnabled;

  const getExternalRowProps = (
    row: MRTRow<RecordType>,
    table: MRTTableInstance<RecordType>,
    staticRowIndex: number
  ): Record<string, unknown> => {
    if (!externalMuiTableBodyRowProps) {
      return {};
    }

    return typeof externalMuiTableBodyRowProps === "function"
      ? (externalMuiTableBodyRowProps({
          row,
          table,
          staticRowIndex,
        }) as Record<string, unknown>)
      : (externalMuiTableBodyRowProps as Record<string, unknown>);
  };

  let customStripedRowsById: Map<string, boolean> | null = null;

  const getCustomStripedRowsById = (table: MRTTableInstance<RecordType>) => {
    if (rowStripingStrategy?.mode !== "resetByPredicate") {
      return undefined;
    }

    if (!customStripedRowsById) {
      customStripedRowsById = buildCustomStripedRowsById(
        table,
        rowStripingStrategy
      );
    }

    return customStripedRowsById;
  };

  const table = useMaterialReactTable({
    columns: displayColums,
    data,
    enableGrouping,
    icons: customizeIcon,
    paginationDisplayMode: "custom",
    enableTableHead,
    enableBottomToolbar: false,
    enableGlobalFilter: false,
    enableFilters: false,
    enableFullScreenToggle: true,
    enableKeyboardShortcuts,
    enablePagination: enablePagination,
    muiCircularProgressProps: {
      Component: <CORELoading />,
    },
    enableEditing,
    editDisplayMode: editDisplayMode,
    state: tableState,
    initialState: tableInitState,
    enableRowSelection: enableRowSelection || !!selectedBulkAction,
    ...(onRowSelectionChange && { onRowSelectionChange: onRowSelectionChange }),
    defaultColumn: {
      minSize: 56,
    },
    enableColumnOrdering: enableColumnOrdering && !selectedBulkAction,
    renderToolbarInternalActions: ({ table }) => (
      <CustomToolbarMenu
        customDownloadCSVButton={customDownloadCSVButton}
        customCSVData={customCSVData}
        csvFileName={csvFileName}
        enableDownloadCSV={enableDownloadCSV}
        table={table}
        data={data}
        testID={testID}
      />
    ),
    renderToolbarAlertBannerContent: ({
      table,
      groupedAlert,
      selectedAlert,
    }) => {
      const { grouping } = table.getState();
      if (grouping.length > 0) {
        return (
          <CustomColumnGroupingToolbar
            testID={testID}
            table={table}
            columns={columns}
          />
        );
      }
      return <></>;
    },
    muiTableBodyCellProps: ({ cell, column, row, table }) => {
      return {
        // `enableEditing` may be a row predicate, and a function is always truthy.
        // isCellEditable is the same gate MRT uses to decide whether to render an
        // editor, so a cell with no editor never flips setIsEditingForDatasheet.
        ...(isCellEditable({ cell, table }) &&
          editDisplayMode === "cell" && {
            [clickToEditMode === "single" ? "onClick" : "onDoubleClick"]:
              () => {
                setIsEditingForDatasheet && setIsEditingForDatasheet(true);
                table.setEditingCell(cell);
                queueMicrotask(() => {
                  const editInputRefsCurrent = table.refs.editInputRefs.current;
                  const textField = editInputRefsCurrent
                    ? editInputRefsCurrent[column.id]
                    : null;
                  textField?.focus();
                  textField?.select?.();
                });
              },
          }),
        ...(setIsEditingForDatasheet && {
          // Plain attributes rather than a `component` override. This used to pass
          // `component: (props) => <td .../>`, an arrow function created afresh on
          // every render, so React saw a new component *type* each time and
          // remounted the whole cell subtree. Any focused edit input inside was
          // destroyed and rebuilt from the row value, which is #2332: increment a
          // few times, an unrelated re-render lands, and the value snaps back to
          // the committed one. It looked random because it tracked re-renders
          // rather than input. MUI's TableCell forwards unknown props to the
          // underlying td, so the selection attributes still land where
          // CORETableEditable's `[data-cell][data-row][data-col]` queries expect.
          "data-cell": true,
          "data-col": column.getIndex(),
          "data-row": row.index,
          onKeyDown: (event) => {
            if (event.key === "Enter") {
              const isEditing = table.getState().editingCell;
              if (isEditing) {
                // To make the COREEditableTable kwow and ignore Enter key to entering edit state
                setIsEditingForDatasheet && setIsEditingForDatasheet(true);
                table.setEditingCell(null);
              }
              event.preventDefault();
            }
          },
          onBlur: (event) => {
            setIsEditingForDatasheet && setIsEditingForDatasheet(false);
            table.setEditingCell(null);
            event.preventDefault();
          },
        }),
        sx: {
          minHeight: getHeightByDensity(table.getState().density),
          paddingY: 0,
        },
      };
    },
    muiTableHeadCellProps: ({ column, table }) => {
      return {
        sx: {
          height: getHeightByDensity(table.getState().density),
        },
      };
    },
    muiTableBodyRowProps: ({ row, table, staticRowIndex }) => {
      const isStripedRow = getIsStripedRow(
        row,
        isTableBandedRows,
        rowStripingStrategy,
        getCustomStripedRowsById(table)
      );
      const resolvedRowStyle = rowStyleResolver
        ? rowStyleResolver({
            row,
            rowIndex: row.index,
            isStripedRow,
          })
        : undefined;

      const baseClassName = classNames(
        isCustomRowStripingEnabled && isStripedRow && "core-table-row-striped",
        resolvedRowStyle?.variant &&
          resolvedRowStyle.variant !== "default" &&
          `core-table-row-variant-${resolvedRowStyle.variant}`,
        resolvedRowStyle?.className
      );

      const coreRowProps: Record<string, unknown> = {
        className: classNames(
          baseClassName,
          rowLink && "core-table-row-linkable"
        ),
        ...(rowLink && {
          sx: {
            ...resolvedRowStyle?.sx,
          },
        }),
        ...(!rowLink &&
          onRow &&
          !selectedBulkAction && {
            onClick: () => {
              onRow(data[parseInt(row.id)]);
            },
            sx: {
              cursor: "pointer",
              ...resolvedRowStyle?.sx,
            },
          }),
        ...(!rowLink &&
          !onRow &&
          resolvedRowStyle?.sx && { sx: resolvedRowStyle.sx }),
      };

      const externalRowProps = getExternalRowProps(row, table, staticRowIndex);
      return mergeTableRowProps(coreRowProps, externalRowProps);
    },
    layoutMode: "grid",
    onDensityChange: (updaterOrValue) => {
      setDensitySetting(updaterOrValue as MRTDensityState);
      const updateValue = {
        ...defaultConfig,
        tableFit: updaterOrValue,
      } as UserPreference;
      addOrUpdateTableConfig && addOrUpdateTableConfig(updateValue);
    },
    ...(enableRememberColumnVisibility && {
      onColumnVisibilityChange: setVisibilitySetting,
    }),
    ...(enableRememberColumnOrder && {
      onColumnOrderChange:
        setColumnOrderSetting as OnChangeFn<ColumnOrderState>,
    }),
    ...(enableRememberGrouping && {
      onGroupingChange: (updaterOrValue) => {
        updateTableSetting(updaterOrValue, "grouping");
      },
    }),
    ...(enableRememberSorting && {
      onSortingChange: (updaterOrValue) => {
        updateTableSetting(updaterOrValue, "sorting");
      },
    }),
    ...props,
    ...(externalToolbarContainerId &&
      isFullScreenMode && {
        renderTopToolbar: undefined,
      }),
  });

  // Mounted after the table instance exists so hydration can measure the rows
  // the table is actually showing.
  const urlStateSync = viewStateURLKey ? (
    <URLSyncedTableStateSync
      urlKey={viewStateURLKey}
      state={{
        ...(enableRememberSorting && sortingState && { sorting: sortingState }),
        ...(enableRememberPaginate &&
          paginationState && { pagination: paginationState }),
        ...(enableRememberColumnVisibility &&
          columnVisibilityState && { columnVisibility: columnVisibilityState }),
      }}
      defaultState={{
        sorting: initialTableSetting.sorting,
        pagination: defaultPaginate,
        columnVisibility: initialTableSetting.columnVisibility,
      }}
      onHydrate={(urlState) => {
        if (enableRememberPaginate && urlState.pagination) {
          const { pageSize } = urlState.pagination;
          // Rows can already be in — a warm query cache on remount, or a param
          // adopted from back/forward — and then no data change follows to clamp
          // for us, so do it here. An empty table still has its page clamped by
          // the data effect below once the rows arrive.
          //
          // Whether they are in has to come from the data prop: with nothing to
          // show yet, MRT stands a page of skeleton placeholders in for the rows,
          // so getRowCount() is not zero and would clamp a shared page down to
          // the first one.
          const rowCount = data.length > 0 ? table.getRowCount() : 0;
          const pageIndex =
            rowCount > 0
              ? Math.min(
                  urlState.pagination.pageIndex,
                  lastPageIndexOf(rowCount, pageSize)
                )
              : urlState.pagination.pageIndex;

          setTableSettingUntilLogout({ pagination: { pageIndex, pageSize } });
          if (enableRememberPageSizeEvenLogginOut) {
            setPageSizeSetting(pageSize);
          }
          // The guard only has to survive the reset that the *first* data
          // arrival triggers. With rows already in, that reset has been and
          // gone, and arming the guard would swallow the next genuine one — the
          // reset a filter change asks for.
          skipPaginationResetFromURLRef.current =
            rowCount === 0 && pageIndex > 0;
        }
        if (enableRememberSorting && urlState.sorting) {
          updateTableSetting(urlState.sorting, "sorting");
        }
        if (enableRememberColumnVisibility && urlState.columnVisibility) {
          setVisibilitySetting(urlState.columnVisibility);
        }
      }}
    />
  ) : null;

  const hasCustomPagination = !!customPagination;
  useDeepCompareEffect(() => {
    // If customPagination is provided, pagination is controlled externally
    // (e.g., by CORETableHook), so we should not auto-reset to avoid double reset
    if (hasCustomPagination) {
      return;
    }

    // For internal pagination: reset to page 1 when data changes
    if (enablePagination && table.getState().pagination.pageIndex > 0) {
      // A page taken from the URL was asked for explicitly, so the first data
      // arrival after opening a shared link must not reset it. Later data
      // changes (a filter being applied) reset as usual.
      if (skipPaginationResetFromURLRef.current) {
        // Only give up the guard once the data prop has arrived: this effect
        // also runs on mount while data is still empty (localStorage already
        // holds the same page index), and consuming it there would let the real
        // data arrival reset the page.
        if (data.length > 0) {
          skipPaginationResetFromURLRef.current = false;

          // The requested page may not exist for this data set — a link shared
          // by someone who sees more rows, or rows removed since. Without
          // clamping the table renders "No records to display" with no way back.
          // getRowCount() rather than data.length: it counts what the table
          // paginates, so a table doing its own filtering or grouping is
          // measured after that, not before.
          const { pageIndex, pageSize } = table.getState().pagination;
          const lastPageIndex = lastPageIndexOf(table.getRowCount(), pageSize);
          if (pageIndex > lastPageIndex) {
            table.setPageIndex(lastPageIndex);

            if (enableRememberPaginate) {
              setTableSettingUntilLogout({
                pagination: { pageIndex: lastPageIndex, pageSize },
              });
            }
          }
        }
        return;
      }

      const currentPageSize = table.getState().pagination.pageSize;

      // Update MRT internal state (immediate UI effect)
      table.setPageIndex(0);

      // Only update if enableRememberPaginate is enabled
      if (enableRememberPaginate && setTableSettingUntilLogout) {
        setTableSettingUntilLogout({
          pagination: {
            pageIndex: 0,
            pageSize: currentPageSize,
          },
        });
      }
    }
  }, [data, hasCustomPagination, enablePagination, enableRememberPaginate]);

  const resetBulkAction = () => {
    table.setRowSelection({});
    setSelectedBulkAction(null);
  };

  const { isFullScreen } = table.getState();
  const density = table.getState().density || "comfortable";
  const densityClass = `core-table-density-${density}`;

  const paginationComponent = customPagination ?? (
    <TablePagination
      testID={testID}
      table={table}
      setTableSettingUntilLogout={setTableSettingUntilLogout}
      setPageSizePermanent={setPageSizeSetting}
      enableRememberPageSizeEvenLogginOut={enableRememberPageSizeEvenLogginOut}
    />
  );

  const toolbarProps = {
    customDownloadCSVButton,
    customCSVData,
    csvFileName,
    enableDownloadCSV,
    table,
    data,
    testID,
  };

  const customToolbarMenuWithPortal = externalToolbarContainerId ? (
    <COREPortal containerId={externalToolbarContainerId}>
      <CustomToolbarMenu
        isPortalUsed={true}
        externalToolbarWrapper={externalToolbarWrapper}
        {...toolbarProps}
      />
    </COREPortal>
  ) : null;

  return (
    <TestIDWrapper
      testID={testID}
      className={classNames(
        "core-table",
        `core-table-theme-${theme}`,
        densityClass,
        { "core-table-full-screen": isFullScreen },
        { "core-table-striped": isDefaultRowStripingEnabled },
        { "core-table-headless": !enableTableHead }
      )}
    >
      {urlStateSync}
      {customToolbarMenuWithPortal}
      <Row>
        <Col span={24} ref={tableRef} style={{ userSelect: "none" }}>
          <MaterialReactTable table={table} />
        </Col>
      </Row>
      {(enablePagination || customPagination || !!bulkActions) && (
        <>
          <Row>
            <Col span={24}>
              <COREDivider space="md" />
            </Col>
          </Row>
          <Row gutter={[spacing.xs, spacing.xs]} justify={"end"}>
            {!!bulkActions && (
              <Col flex={"auto"}>
                <BulkActionControl
                  bulkActions={bulkActions}
                  table={table}
                  selectedBulkAction={selectedBulkAction}
                  setSelectedBulkAction={setSelectedBulkAction}
                  resetBulkAction={resetBulkAction}
                  testID={testID}
                />
              </Col>
            )}
            {(enablePagination || customPagination) && (
              <Col>{paginationComponent}</Col>
            )}
          </Row>
        </>
      )}
    </TestIDWrapper>
  );
};
